import { ServerConfig } from "@/config/config";
import { Company } from "@/models/company";
import { Axios } from "@/services/auth-service";
import { InvoiceAddress } from "@/models/invoice-address";
import { CompanyModule, IModuleMetaData } from "@/models/company-module";
import { ViewSettings } from "@/models/view-settings";
import { ClientMailNotifications } from "@/models/client-mail-notifications";
import { IPaginationData } from "@/utils/browse/pagination-data";
import { IPaginatedResponse } from "@/utils/browse/pagination-response";
import { CRM } from "@/models/crm";
import { CompanyFilters } from "@/utils/company-filters";
import { DiscoveredUsFrom } from "@/models/discovered-us-from";
import { CompanyDossierService } from "./company-dossier-service";
import { CompanyVars } from "@/models/company-vars";
import { Dossier } from "@/models/dossier";

export interface ICreateCompanyResult {
	company:Company;
	apiKey:string;
}

export class CompanyServiceClass{
	readonly url = ServerConfig.host + "/admin/company";
	private getCrmUrl(companyId:number){
		return `${this.url}/${companyId}/crm`;
	}

	readonly mainAppUrl = ServerConfig.mainAppHost + "/company"

	async getViewSettings():Promise<ViewSettings>{
		let res = await Axios.get(`${this.url}/view-settings`);
		return new ViewSettings(res.data);
	}

	async getStoredFilters():Promise<CompanyFilters>{
		let res = await Axios.get(`${this.url}/filters`);
		let filters = new CompanyFilters(res.data);
		return filters;
	}

	async getCompanies(limit: number, page: number, orderBy: string, orderDirection: boolean, filters?:CompanyFilters): Promise<{ companies: Company[], total: number }> {
		if (orderBy == "id"){
			orderBy = "this.ID";
		}
		let result = await Axios.get(this.url, {
			params: {
				limit,
				offset: limit * (page-1),
				order: orderBy,
				orderDirection: orderDirection ? "ASC" : "DESC",
				filters: filters ? filters.getJSON() : null
			}
		});
		let data = {
			companies: result.data.data.map((c: any) => new Company(c)),
			total: result.data.records
		};
		return data;
	}

	async downloadExport(filters?:CompanyFilters):Promise<void>{
		let result = await Axios.get(`${this.url}/export`, {
			params: {
				filters: filters ? filters.getJSON() : null
			}
		});
		let file = new Blob([result.data], {type: "text/csv"});
		let a = document.createElement("a");
		a.href = URL.createObjectURL(file);
		let now = new Date();
		let date = now.getDate().toLocaleString(undefined, {minimumIntegerDigits: 2});
		let month = now.getMonth().toLocaleString(undefined, {minimumIntegerDigits: 2});
		a.download = `account-export-${now.getFullYear()}${month}${date}.csv`;
		a.click();
		URL.revokeObjectURL(a.href);
		a.remove();
	}

	async getCompany(id: number): Promise<Company> {
		let result = await Axios.get(`${this.url}/${id}`);
		return new Company(result.data);
	}

	async getRawCompany(id:number):Promise<any> {
		let result = await Axios.get(`${this.url}/${id}`);
		return result.data;
	}

	async getCompanyByDossierID(dossierId:number):Promise<Company>{
		let result = await Axios.get(`${this.url}/by-dossier-id/${dossierId}`);
		return new Company(result.data);
	}

	async searchDossiers(query:string):Promise<{ID:number, Name:string, CompanyID:number, CompanyName:string}[]>{
		let result = await Axios.get(`${this.url}/search-dossiers`, {params: {q: query}});
		return result.data || [];
	}

	async createCompany(data:{
		companyname:string,
		firstname:string,
		lastname:string,
		email:string,
		phone:string,
		password:string,
		sendEmailToUser:boolean,
		isUserActiveByDefault:boolean,
		activeUntil?:Date,
		createApiKey:boolean,
		isAccountant:boolean,
		dateCreated:Date | null,
		dbId:null | number
	}): Promise<ICreateCompanyResult> {
		let result = await Axios.post(this.url, data);
		let company = new Company(result.data.company);
		for (let dossier of company.dossiers){
			CompanyDossierService.initDatabase(dossier.id);
		}
		return {
			company,
			apiKey: result.data.apiKey
		};
	}

	async updateCompany(company: Company):Promise<Company> {
		let result = await Axios.put(`${this.url}/${company.id}`, company.getJSON());
		return new Company(result.data);
	}

	async deleteCompany(company:Company):Promise<void> {
		await Axios.delete(`${this.url}/${company.id}`);
	}

	async addModulesToCompany(company: Company, modules: CompanyModule[]): Promise<Company> {
		let result = await Axios.post(`${this.url}/${company.id}/modules`, modules.map(m => m.id));
		return new Company(result.data);
	}

	async removeModulesFromCompany(company: Company, modules: CompanyModule[]): Promise<Company> {
		let result = await Axios.delete(`${this.url}/${company.id}/modules`, {  data: modules.map(m => m.id) });
		return new Company(result.data);
	}

	async updateModuleOfCompany(company:Company, moduleId:number, metadata:IModuleMetaData):Promise<CompanyModule>{
		let result = await Axios.put(`${this.url}/${company.id}/modules/${moduleId}`, metadata);
		return new CompanyModule(result.data);
	}

	async getInvoiceAddress(company:Company):Promise<InvoiceAddress> {
		let result = await Axios.get(`${this.url}/${company.id}/invoice-address`);
		return new InvoiceAddress(result.data);
	}

	// Resets the modules after a tier change
	async resetModules(company:Company):Promise<Company>{
		let result = await Axios.post(`${this.url}/${company.id}/reset-modules`);
		return new Company(result.data);
	}

	async getClientMailNotificationStates(company:Company):Promise<ClientMailNotifications>{
		let result = await Axios.get(`${this.url}/${company.id}/client-mail-notifications`);
		return new ClientMailNotifications(result.data);
	}

	async sendInitialSpExpiryMail(company:Company):Promise<void>{
		await Axios.post(`${this.url}/${company.id}/send-initial-sp-expiry-email`);
	}

	async sendSpExpiryMail(company:Company):Promise<void>{
		await Axios.post(`${this.url}/${company.id}/send-sp-expiry-email`);
	}

	async getCrms(companyId:number, pagination:IPaginationData):Promise<IPaginatedResponse<CRM>>{
		let response = await Axios.get(`${this.getCrmUrl(companyId)}`, {
			params: {
				...pagination,
				orderDirection: pagination.ascending ? "ASC" : "DESC",
			}
		});
		let result:IPaginatedResponse<CRM> = {
			data: response.data.data.map((d:any)=>new CRM(d)),
			records: response.data.records
		};
		return result;
	}

	async createCrm(companyId:number, crm:CRM):Promise<CRM>{
		let response = await Axios.post(`${this.getCrmUrl(companyId)}`, crm.getJSON());
		return new CRM(response.data);
	}

	async setDiscoveredUsFrom(company:Company, discoveredUsFrom:DiscoveredUsFrom):Promise<DiscoveredUsFrom>{
		let response = await Axios.post(`${this.url}/${company.id}/set-discovered-us-from`, discoveredUsFrom.getJSON());
		return new DiscoveredUsFrom(response.data);
	}

	async getVars(company:Company):Promise<CompanyVars>{
		let result = await Axios.get(`${this.url}/${company.id}/vars`);
		return new CompanyVars(result.data);
	}

	async updateVars(companyId:number, vars:CompanyVars):Promise<CompanyVars>{
		let result = await Axios.put(`${this.url}/${companyId}/vars`, vars);
		return new CompanyVars(result.data);
	}

	async moteToOtherDb(company:Company, dbId:number):Promise<void>{
		await Axios.post(`${this.mainAppUrl}/${company.id}/move-to-other-db`, {targetDbId: dbId});
	}

	async isMovingToAltDb(company:Company):Promise<boolean>{
		let result = await Axios.get(`${this.mainAppUrl}/${company.id}/is-being-moved`);
		return result.data.Result;
	}

	async getMovedToButNotDeletedDossiers():Promise<Dossier[]>{
		let result = await Axios.get(`${this.url}/moved-to-db2-but-not-deleted`);
		return result.data.map((r:any)=>new Dossier(r));
	}

	async deleteDossier(dossierId:number):Promise<void>{
		await Axios.post(`${this.url}/delete-moved-dossier/${dossierId}`);
	}

	public async queueMoveToOtherDb(company:Company, targetDbId:number):Promise<void>{
		await Axios.post(`${this.mainAppUrl}/${company.id}/queue-move-to-other-db`, {targetDbId});
	}

	public async unqueueMoveToOtherDb(company:Company | number):Promise<void>{
		if (typeof(company) == "object") {
			company = company.id;
		}
		await Axios.post(`${this.mainAppUrl}/${company}/unqueue-move-to-other-db`);
	}

	public async getQueuedCompanyDbMoves():Promise<{companyId:number, targetDbId:number}[]> {
		let result = await Axios.get(`${this.mainAppUrl}/queued-company-db-moves`);
		return result.data || [];
	}

	public async searchCompanies(query:string):Promise<Company[]>{
		let result = await Axios.get(`${this.url}/search`, {params: {q: query}});
		return ((result.data || []) as any[]).map(c=>new Company(c));
	}
};

export const CompanyService = new CompanyServiceClass();
